import makeStyles from "@mui/styles/makeStyles";
import DataGridView from "components/grid/DataGridView";
import { RenderableColumnDefinition } from "components/grid/gridTypings";
import { IColumnProps } from "devextreme-react/data-grid";

import { Tooltip } from "@mui/material";

import { DateComponent, DateVariant } from "components/base/DateComponent";
import { LoadOptions } from "devextreme/data";
import CustomStore from "devextreme/data/custom_store";
import { useSolutionKey } from "features/solution";
import _ from "lodash";

const useStyles = makeStyles({
  flex: { flex: "1 1 auto", overflow: "auto" },
  tooltip: {
    backgroundColor: "#fff",
    border: "solid 1px rgb(0, 0, 0, 0.9)",
    borderRadius: 0,
    color: "rgb(0, 0, 0, 0.9)"
  }
});

type TableSetGridProps<TVariables, TTable> = {
  useCaseKey: string;
  useCaseVersion?: string | null;
  dataKey?: string;
  calculationId?: string;
  name: string;
  columnDefs?: (
    schemaName: string
  ) => RenderableColumnDefinition<IColumnProps, {}>[];
  loadData: (variables: TVariables) => Promise<TTable | null>;
};

function TableSetGrid<TVariables, TTable>({
  useCaseKey,
  useCaseVersion = null,
  dataKey,
  calculationId,
  name,
  columnDefs,
  loadData
}: TableSetGridProps<TVariables, TTable>) {
  const solutionKey = useSolutionKey();
  const { flex, tooltip } = useStyles({});

  const dataSetViewModelToObjectRows = (table: TTable | undefined) => {
    const rows = (table as { rows?: unknown[] })?.rows;
    if (Array.isArray(rows)) {
      const columnNames = columnDefs?.(name)
        .map(({ colDef: { dataField } }) => dataField)
        .filter((columnName): columnName is string => columnName !== undefined);
      const rowObjects = rows.map(
        row => row && _.zipObjectDeep(columnNames, row as unknown[])
      );
      return rowObjects;
    }
  };

  const load = async (loadOptions: LoadOptions) => {
    const skip = loadOptions.skip;
    const take = loadOptions.take;
    const table = dataKey
      ? await loadData({
          solutionKey,
          useCaseKey,
          useCaseVersion,
          keys: [dataKey],
          names: [name],
          skip,
          take
        } as TVariables)
      : await loadData({
          solutionKey,
          useCaseKey,
          useCaseVersion,
          calculationId,
          names: [name],
          skip,
          take
        } as TVariables);
    if (
      table &&
      (table as unknown as { rowCount?: number }).rowCount !== undefined
    ) {
      const rows = dataSetViewModelToObjectRows(table);
      return {
        data: rows,
        totalCount: (table as unknown as { rowCount: number }).rowCount
      };
    }
  };

  const RenderableColumns = columnDefs?.(name).map(col => {
    if (col.typeName === "dateTime") {
      return _.omit(
        {
          ...col,
          render: (params: {
            data: { [key: string]: string | Date | null | undefined };
          }) => {
            const dataField = col.colDef.dataField;
            const value = dataField ? params.data[dataField] : undefined;
            const date = value
              ? value instanceof Date
                ? value
                : new Date(value)
              : null;
            return date ? (
              <Tooltip
                title={
                  <DateComponent date={date} variant={DateVariant.DETAILED} />
                }
                classes={{ tooltip }}
              >
                <div>
                  <DateComponent date={date} variant={DateVariant.MEDIUM} />
                </div>
              </Tooltip>
            ) : null;
          }
        },
        "typeName"
      );
    } else return _.omit(col, "typeName");
  });

  return (
    <div className={flex}>
      <DataGridView
        dataSource={new CustomStore({ load })}
        columns={RenderableColumns}
        allowSelection={false}
        showFilterRow={false}
      />
    </div>
  );
}

export default TableSetGrid;
